import { LESSONS, LESSON_IDS } from '../../../games/analysis/lessons.ts';
import type { LessonId } from '../../../games/analysis/lessons.ts';
import type { GameSpec } from '../../../games/spec/schema.ts';
import { Board } from '../match/Board.tsx';
import type { BoardAnnotation } from '../match/Board.tsx';
import { themeVariables } from '../match/presentation.ts';
import { DRILLS_PER_LESSON, useLessons } from './useLessons.ts';
import type { LearnView } from './useLessons.ts';

export interface LearnPageProps {
  readonly spec: GameSpec;
  readonly onExit: () => void;
  /** Runs the engine. Defaults to a real worker; tests pass their own. */
  readonly createWorker?: () => Worker;
}

/** Columns are 1-based on screen; the engine counts from zero. */
function columnName(param: number): string {
  return String(param + 1);
}

/**
 * What the screen says about where you are.
 *
 * Facts, not praise. "Right" and a reason beats "Well done!", because the
 * reason is the part that transfers to the next game.
 */
function statusLine(view: LearnView): string {
  const lesson = view.lesson;
  switch (view.phase) {
    case 'seeking':
      return 'Finding a position\u2026';
    case 'checking':
      return 'Checking\u2026';
    case 'right':
      return lesson ? lesson.why : 'That is the one.';
    case 'wrong':
      return view.walkedIn
        ? 'Not that one \u2014 that move actually loses on the spot. Look again.'
        : 'Not that one. The idea is still on the board \u2014 look again.';
    case 'stuck':
      return 'No position could be found for this one right now.';
    case 'finished':
      return view.correct === view.total
        ? `All ${String(view.total)} without help. That idea is yours.`
        : `${String(view.correct)} of ${String(view.total)} unaided. Worth another run.`;
    default:
      return lesson ? lesson.task : '';
  }
}

/**
 * Learn the ideas: openings, threats, forks and traps.
 *
 * The teaching order is name → explain → find it. Every other surface in the
 * product starts from a position and tells you what is true of it, which builds
 * facts rather than vocabulary. Here the idea is named first and drilled second,
 * because an idea you cannot name is one you cannot go looking for during a
 * game.
 *
 * Nothing on this screen is invented. Every answer is computed from the board
 * by the same code that judges it, so a lesson cannot recommend something the
 * engine would then mark wrong.
 */
export function LearnPage({ spec, onExit, createWorker }: LearnPageProps) {
  const { view, choose, begin, answer, show, again, next, menu } = useLessons({
    spec,
    ...(createWorker ? { createWorker } : {}),
  });

  const waiting = view.phase === 'seeking' || view.phase === 'checking';
  const asking = view.phase === 'asking';

  /*
   * The answer, drawn on the board rather than described.
   *
   * A column number has to be translated back into a place before it is any
   * use; a ghost disc is already there.
   */
  const hint: BoardAnnotation | null =
    view.shown === null || view.yourSeat === null
      ? null
      : {
          column: view.shown,
          seat: view.yourSeat,
          label: `the answer is column ${columnName(view.shown)}`,
        };

  if (view.phase === 'choosing') {
    return (
      <section className="learn" aria-labelledby="learn-heading" style={themeVariables(spec)}>
        <header className="learn__head">
          <h2 id="learn-heading" className="learn__heading">
            Learn the ideas
          </h2>
          {/* Counted from the curriculum itself, so adding a lesson cannot
              leave the sentence quietly lying about how many there are. */}
          <p className="learn__intro">
            {LESSON_IDS.length} ideas that decide games. Each one is named, explained, then put in
            front of you {DRILLS_PER_LESSON} times.
          </p>
        </header>

        <ul className="learn__menu">
          {LESSON_IDS.map((id, index) => {
            const lesson = LESSONS[id];
            return (
              <li key={id}>
                <button
                  type="button"
                  className="learn__pick"
                  onClick={() => choose(id as LessonId)}
                >
                  {/* Numbered because the order is the curriculum: you learn
                      where to play before you learn what to stop. */}
                  <span className="learn__pickNo" aria-hidden="true">
                    {index + 1}
                  </span>
                  <span className="learn__pickText">
                    <span className="learn__pickTitle">{lesson.title}</span>
                    <span className="learn__pickIdea">{lesson.idea}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <footer className="learn__exit">
          <button type="button" className="button button--quiet" onClick={onExit}>
            Done
          </button>
        </footer>
      </section>
    );
  }

  const lesson = view.lesson;

  /*
   * The idea on its own, before any board.
   *
   * A deliberate pause. Dropping straight onto a position would make this
   * another puzzle screen, and the player would leave able to do the thing
   * without ever learning what it is called.
   */
  if (view.phase === 'reading' && lesson) {
    return (
      <section className="learn" aria-labelledby="learn-heading" style={themeVariables(spec)}>
        <header className="learn__head">
          <h2 id="learn-heading" className="learn__heading">
            {lesson.title}
          </h2>
        </header>

        <div className="learn__card">
          <p className="learn__idea">{lesson.idea}</p>
          <p className="learn__then">
            Next: {String(view.total)} positions where this is the answer.
          </p>
        </div>

        <div className="learn__actions">
          <button type="button" className="button button--primary" onClick={begin}>
            Show me a position
          </button>
          <button type="button" className="button button--quiet" onClick={menu}>
            Pick another idea
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="learn" aria-labelledby="learn-heading" style={themeVariables(spec)}>
      <header className="learn__head">
        <h2 id="learn-heading" className="learn__heading">
          {lesson ? lesson.title : 'Learn the ideas'}
        </h2>
        {view.drillNumber > 0 ? (
          <p className="learn__progress">
            Position {String(Math.min(view.drillNumber, view.total))} of {String(view.total)}
          </p>
        ) : null}
      </header>

      <div className="learn__layout">
        <div className="learn__stage">
          <Board
            spec={spec}
            board={view.board}
            yourSeat={view.yourSeat}
            legalColumns={view.legalColumns}
            pendingColumn={null}
            lastMoveIndex={view.lastMoveCell}
            winningLine={null}
            interactive={asking}
            onDrop={answer}
            annotation={hint}
            columnControls={view.drill !== null}
          />
        </div>

        <div className="learn__side">
          {/*
            One live region. The board announces its own squares; a second
            channel narrating the same taps would talk over the sentence that
            says what to do.
          */}
          <p className="learn__status" role="status">
            {waiting ? <span className="spinner" aria-hidden="true" /> : null}
            {statusLine(view)}
          </p>

          {view.error !== null ? <p className="learn__error">{view.error}</p> : null}

          <div className="learn__actions">
            {view.phase === 'right' ? (
              <button type="button" className="button button--primary" onClick={next}>
                {view.drillNumber >= view.total
                  ? 'Finish'
                  : view.nextReady
                    ? 'Next position'
                    : 'Next position (finding\u2026)'}
              </button>
            ) : null}

            {view.phase === 'wrong' ? (
              <button type="button" className="button button--primary" onClick={again}>
                Look again
              </button>
            ) : null}

            {(asking || view.phase === 'wrong') && view.shown === null ? (
              <button type="button" className="button button--quiet" onClick={show}>
                Show me
              </button>
            ) : null}

            {view.phase === 'finished' || view.phase === 'stuck' ? (
              <button type="button" className="button button--primary" onClick={menu}>
                Pick another idea
              </button>
            ) : null}
          </div>

          {/*
            The counted claim behind the centre lesson, shown where it applies.
            "Seven winning lines" means nothing on its own; the comparison is
            the entire argument, and it is counted rather than asserted.
          */}
          {lesson?.id === 'centre_control' && view.drill?.lines != null && view.drill.fewest != null ? (
            <p className="learn__note">
              That square sits in {String(view.drill.lines)} winning lines. The weakest square you
              could play sits in {String(view.drill.fewest)}.
              {view.drill.solvedGame
                ? ' On this board, opening in the centre is a proven first-player win.'
                : ''}
            </p>
          ) : null}

          <p className="learn__tally">
            {view.drillNumber === 0
              ? ''
              : `${String(view.correct)} of ${String(view.drillNumber)} found without help.`}
          </p>

          <footer className="learn__exit">
            <button type="button" className="button button--quiet" onClick={menu}>
              Back to the list
            </button>
            <button type="button" className="button button--quiet" onClick={onExit}>
              Done
            </button>
          </footer>
        </div>
      </div>
    </section>
  );
}
